export function beginProjectSelection<T>(project: T) {
  return {
    current: project,
    view: 'outline' as const,
    refreshing: true,
  };
}

export function shouldApplySelectionResponse(
  requestId: number,
  latestRequestId: number,
  requestedProjectId?: string,
  routeProjectId?: string,
) {
  return requestId === latestRequestId
    && (requestedProjectId === undefined || requestedProjectId === routeProjectId);
}

export type ProjectRequestKind = 'first-page' | 'append' | 'detail' | 'references';

export type ProjectRequestToken = Readonly<{
  identityEpoch: number;
  generation: number;
}>;

export type ProjectMutationToken = Readonly<{
  identityEpoch: number;
  projectId: string;
  generation: number;
}>;

export type ProjectRecoveryToken = Readonly<{
  identityEpoch: number;
  projectId: string;
  generation: number;
}>;

export class ProjectRequestGate {
  private identityEpoch = 0;
  private readonly generations = new Map<ProjectRequestKind, number>();
  private readonly mutationGenerations = new Map<string, number>();
  private readonly recoveryGenerations = new Map<string, number>();
  private readonly mutationCleanups = new Map<string, Readonly<{ generation: number; cleanup: () => void }>>();
  private activeFirstPage?: ProjectRequestToken;
  private projectListReady = false;
  private nextProjectOperationGeneration = 0;

  captureIdentity() {
    return this.identityEpoch;
  }

  isIdentityCurrent(identityEpoch: number) {
    return identityEpoch === this.identityEpoch;
  }

  advanceIdentity() {
    const cleanups = [...this.mutationCleanups.values()];
    this.identityEpoch += 1;
    this.generations.clear();
    this.mutationGenerations.clear();
    this.recoveryGenerations.clear();
    this.mutationCleanups.clear();
    this.activeFirstPage = undefined;
    this.projectListReady = false;
    for (const { cleanup } of cleanups) cleanup();
    return this.identityEpoch;
  }

  beginFirstPage() {
    this.invalidate('append');
    this.projectListReady = false;
    const token = this.begin('first-page');
    this.activeFirstPage = token;
    return token;
  }

  finishFirstPage(token: ProjectRequestToken, succeeded: boolean) {
    if (this.activeFirstPage === token && this.isCurrent('first-page', token)) {
      this.activeFirstPage = undefined;
      this.projectListReady = succeeded;
    }
  }

  beginAppend() {
    if (!this.projectListReady || (this.activeFirstPage && this.isCurrent('first-page', this.activeFirstPage))) return undefined;
    return this.begin('append');
  }

  invalidateProjectLists() {
    this.invalidate('first-page');
    this.invalidate('append');
    this.activeFirstPage = undefined;
    this.projectListReady = false;
  }

  beginProjectMutation(projectId: string, cleanup?: () => void): ProjectMutationToken {
    const superseded = this.mutationCleanups.get(projectId);
    this.mutationCleanups.delete(projectId);
    superseded?.cleanup();
    const generation = ++this.nextProjectOperationGeneration;
    this.mutationGenerations.set(projectId, generation);
    if (cleanup) this.mutationCleanups.set(projectId, { generation, cleanup });
    return { identityEpoch: this.identityEpoch, projectId, generation };
  }

  invalidateProjectMutation(projectId: string) {
    const invalidated = this.mutationCleanups.get(projectId);
    this.mutationCleanups.delete(projectId);
    invalidated?.cleanup();
    this.mutationGenerations.delete(projectId);
  }

  finishProjectMutation(token: ProjectMutationToken) {
    if (!this.isProjectMutationCurrent(token)) return false;
    const current = this.mutationCleanups.get(token.projectId);
    if (current?.generation === token.generation) {
      this.mutationCleanups.delete(token.projectId);
      current.cleanup();
    }
    this.mutationGenerations.delete(token.projectId);
    return true;
  }

  isProjectMutationCurrent(token: ProjectMutationToken) {
    return this.isIdentityCurrent(token.identityEpoch)
      && token.generation === this.mutationGenerations.get(token.projectId);
  }

  beginProjectRecovery(projectId: string): ProjectRecoveryToken {
    const generation = ++this.nextProjectOperationGeneration;
    this.recoveryGenerations.set(projectId, generation);
    return { identityEpoch: this.identityEpoch, projectId, generation };
  }

  invalidateProjectRecovery(projectId: string) {
    this.recoveryGenerations.delete(projectId);
  }

  finishProjectRecovery(token: ProjectRecoveryToken) {
    if (!this.isProjectRecoveryCurrent(token)) return false;
    this.recoveryGenerations.delete(token.projectId);
    return true;
  }

  clearProjectState(projectId: string) {
    this.invalidateProjectMutation(projectId);
    this.invalidateProjectRecovery(projectId);
  }

  isProjectRecoveryCurrent(token: ProjectRecoveryToken) {
    return this.isIdentityCurrent(token.identityEpoch)
      && token.generation === this.recoveryGenerations.get(token.projectId);
  }

  begin(kind: ProjectRequestKind): ProjectRequestToken {
    const generation = (this.generations.get(kind) ?? 0) + 1;
    this.generations.set(kind, generation);
    return { identityEpoch: this.identityEpoch, generation };
  }

  invalidate(kind: ProjectRequestKind) {
    this.generations.set(kind, (this.generations.get(kind) ?? 0) + 1);
  }

  isCurrent(kind: ProjectRequestKind, token: ProjectRequestToken) {
    return this.isIdentityCurrent(token.identityEpoch)
      && token.generation === this.generations.get(kind);
  }
}
